import React, { useState, useEffect } from 'react';
import { getOnboardingRequests } from '../../services/platformApi';
import OnboardingDetail from './OnboardingDetail';
import './AdminOnboarding.css';

/* ── Inline SVG icons (no icon library, no emoji) ── */
const Ico = {
  cube: <svg viewBox="0 0 24 24"><path d="M3 7l9-4 9 4v10l-9 4-9-4z" /><path d="M3 7l9 4 9-4M12 11v10" /></svg>,
  search: <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>,
  refresh: <svg viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.5 9a9 9 0 0114.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0020.5 15" /></svg>,
  clock: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  check: <svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></svg>,
  users: <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>,
  arrow: <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>,
  inbox: <svg viewBox="0 0 24 24"><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" /></svg>,
  warn: <svg viewBox="0 0 24 24"><path d="M10.3 3.9l-8 14A2 2 0 004 21h16a2 2 0 001.7-3.1l-8-14a2 2 0 00-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>,
};

// Deterministic avatar gradient class from a name/email
const AV_CLASSES = ['aod-av0', 'aod-av1', 'aod-av2', 'aod-av3', 'aod-av4'];
const avClass = (seed) => {
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV_CLASSES[h % AV_CLASSES.length];
};
const initials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AdminOnboarding = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filter, setFilter] = useState('PENDING'); // PENDING | APPROVED | ALL
  const [search, setSearch] = useState('');

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getOnboardingRequests();
      setRequests(data.requests || data.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load onboarding requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Normalize (camelCase or snake_case)
  const norm = (req) => ({
    id: req.id,
    fullName: req.fullName || req.full_name || '-',
    email: req.email || req.companyEmail || req.company_email || '-',
    department: req.department || '-',
    designation: req.designation || '-',
    status: req.status || 'PENDING',
    role: req.role || '',
    employeeId: req.employeeId || req.employee_id || '',
    submittedAt: req.createdAt || req.created_at || req.submittedAt || req.submitted_at || '',
  });

  const normalized = requests.map(norm);
  const isApproved = (s) => s === 'ACTIVE' || s === 'APPROVED';

  // Filter by tab first, then by search (name OR email — case-insensitive)
  const q = search.trim().toLowerCase();
  const filteredRequests = normalized
    .filter((req) => {
      if (filter === 'APPROVED') return isApproved(req.status);
      if (filter === 'PENDING') return req.status === 'PENDING';
      return true; // ALL
    })
    .filter((req) => {
      if (!q) return true;
      return (
        req.fullName.toLowerCase().includes(q) ||
        req.email.toLowerCase().includes(q) ||
        (req.employeeId || '').toLowerCase().includes(q)
      );
    });

  const pendingCount = normalized.filter((r) => r.status === 'PENDING').length;
  const approvedCount = normalized.filter((r) => isApproved(r.status)).length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date)) return '-';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const statusMeta = (status) => {
    if (isApproved(status)) return { cls: 'aod-sb--active', rail: 'aod-rail--active', label: status === 'ACTIVE' ? 'Active' : 'Approved' };
    if (status === 'DISABLED') return { cls: 'aod-sb--disabled', rail: 'aod-rail--disabled', label: 'Disabled' };
    return { cls: 'aod-sb--pending', rail: 'aod-rail--pending', label: 'Pending' };
  };

  if (selectedRequest) {
    return (
      <OnboardingDetail
        requestId={selectedRequest.id}
        onBack={() => {
          setSelectedRequest(null);
          fetchRequests();
        }}
      />
    );
  }

  return (
    <div className="aod">
      {/* Premium header band */}
      <div className="aod-band">
        <div className="aod-band__row">
          <div className="aod-brand">
            <div className="aod-brand__logo">{Ico.cube}</div>
            <div>
              <h1>Onboarding Requests</h1>
              <p>Review, approve &amp; manage employee registrations</p>
            </div>
          </div>
          <div className="aod-band__r">
            <div className="aod-gsearch">
              {Ico.search}
              <input
                type="text"
                placeholder="Search name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="aod-gsearch__clear" onClick={() => setSearch('')} title="Clear">×</button>
              )}
            </div>
            <button className="aod-gbtn" onClick={fetchRequests} disabled={loading} title="Refresh">
              {Ico.refresh}
            </button>
          </div>
        </div>
      </div>

      <div className="aod-wrap">
        {/* Stats */}
        <div className="aod-stats">
          <div className="aod-card">
            <div className="aod-card__top">
              <div className="aod-card__ic aod-ic-amber">{Ico.clock}</div>
            </div>
            <div className="aod-card__num">{pendingCount}</div>
            <div className="aod-card__lbl">Pending Review</div>
          </div>
          <div className="aod-card">
            <div className="aod-card__top">
              <div className="aod-card__ic aod-ic-green">{Ico.check}</div>
            </div>
            <div className="aod-card__num">{approvedCount}</div>
            <div className="aod-card__lbl">Approved &amp; Active</div>
          </div>
          <div className="aod-card">
            <div className="aod-card__top">
              <div className="aod-card__ic aod-ic-navy">{Ico.users}</div>
            </div>
            <div className="aod-card__num">{normalized.length}</div>
            <div className="aod-card__lbl">Total Requests</div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="aod-toolbar">
          <div className="aod-seg">
            <button className={filter === 'PENDING' ? 'on' : ''} onClick={() => setFilter('PENDING')}>
              Pending {pendingCount > 0 && <span className="aod-pill">{pendingCount}</span>}
            </button>
            <button className={filter === 'APPROVED' ? 'on' : ''} onClick={() => setFilter('APPROVED')}>
              Approved {approvedCount > 0 && <span className="aod-pill">{approvedCount}</span>}
            </button>
            <button className={filter === 'ALL' ? 'on' : ''} onClick={() => setFilter('ALL')}>
              All
            </button>
          </div>
          <span className="aod-count">
            Showing {filteredRequests.length} of {normalized.length} requests
          </span>
        </div>

        {error && (
          <div className="aod-error">
            {Ico.warn} <span>{error}</span>
            <button onClick={fetchRequests}>Retry</button>
          </div>
        )}

        {loading && (
          <div className="aod-loading">
            <div className="aod-spinner" />
            <span>Loading requests…</span>
          </div>
        )}

        {!loading && !error && (
          filteredRequests.length === 0 ? (
            <div className="aod-empty">
              <div className="aod-empty__ic">{Ico.inbox}</div>
              <h3>{q ? 'No matching requests' : `No ${filter.toLowerCase()} requests`}</h3>
              <p>
                {q
                  ? 'Try a different name or email.'
                  : filter === 'PENDING'
                  ? 'All caught up! No pending onboarding requests.'
                  : 'No records found for this filter.'}
              </p>
            </div>
          ) : (
            <div className="aod-list">
              {filteredRequests.map((req) => {
                const meta = statusMeta(req.status);
                return (
                  <div className="aod-row" key={req.id} onClick={() => setSelectedRequest(req)}>
                    <span className={`aod-rail ${meta.rail}`} />
                    <div className="aod-who">
                      <span className={`aod-av ${avClass(req.email || req.fullName)}`}><span>{initials(req.fullName)}</span></span>
                      <div>
                        <b>{req.fullName}</b>
                        <span>{req.email}</span>
                      </div>
                    </div>
                    <div className="aod-kv">
                      <small>Emp ID</small>
                      <b>{req.employeeId || '—'}</b>
                    </div>
                    <div className="aod-kv">
                      <small>Department</small>
                      <span className="aod-chip">{req.department}</span>
                    </div>
                    <div className="aod-kv">
                      <small>Submitted</small>
                      <b>{formatDate(req.submittedAt)}</b>
                    </div>
                    <span className={`aod-sb ${meta.cls}`}><i />{meta.label}</span>
                    <button
                      className={`aod-view ${meta.label === 'Pending' ? '' : 'aod-view--ghost'}`}
                      onClick={(e) => { e.stopPropagation(); setSelectedRequest(req); }}
                    >
                      {meta.label === 'Pending' ? 'Review' : 'View'} {Ico.arrow}
                    </button>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AdminOnboarding;
